import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import { CoachingApi } from '../../api/coaching.api';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseFilter } from '../../models/filter';
import { BrowseSorting } from '../../models/sorting';
import { Sport } from '../../models/sport';
import { Store } from '../../../../shared/store';
import { CoachingState } from './coaching.state';
import { Coaching } from '@kinomap-dev/ng-ui-lib';

const coachingState: CoachingState = new CoachingState();

@Injectable({ providedIn: 'root' })
export class CoachingStore extends Store<CoachingState> {
  coachings$ = this.state$.pipe(
    map((s) => {
      return s.coachings;
    })
  );
  isLoadingCoachings$ = this.state$.pipe(map((s) => s.isLoadingCoachings));
  areCoachingsComplete$ = this.state$.pipe(map((s) => s.areCoachingsComplete));

  constructor(private coachingApi: CoachingApi) {
    super(coachingState);
  }

  // SET COACHINGS

  setCoachings(nextCoachings: Coaching[]): void {
    this.setState({ ...this.state, coachings: nextCoachings });
  }

  setIsLoadingCoachings(nextIsLoadingCoachings: boolean): void {
    this.setState({
      ...this.state,
      isLoadingCoachings: nextIsLoadingCoachings,
    });
  }

  setAreCoachingsComplete(nextAreCoachingsComplete: boolean): void {
    this.setState({
      ...this.state,
      areCoachingsComplete: nextAreCoachingsComplete,
    });
  }

  // GET COACHINGS FROM THE API

  async getCoachings(
    {
      newSelectedSport,
      newFilters,
      newSorting,
      nextPage,
    }: {
      newSelectedSport?: Sport;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      nextPage: boolean;
    } = { nextPage: false }
  ): Promise<void> {
    if (nextPage && this.state.areCoachingsComplete) {
      return;
    }

    let goOn: boolean = !this.state.isLoadingCoachings;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingCoachings;
    }

    if (!nextPage) {
      this.setCoachings([]);
      this.setAreCoachingsComplete(false);
    }

    this.setIsLoadingCoachings(true);

    try {
      const fetchedCoachings: Coaching[] = await firstValueFrom(
        this.coachingApi.getCoachings$(
          newSelectedSport,
          newFilters,
          newSorting,
          nextPage
        )
      );
      const newCoachings: Coaching[] =
        this.getNewCoachingsWithoutDuplicate(fetchedCoachings);
      this.setCoachings(newCoachings);

      this.setAreCoachingsComplete(this.coachingApi.isLastPageLoaded);
    } catch (err) {
      this.setAreCoachingsComplete(true);
    } finally {
      this.setIsLoadingCoachings(false);
    }
  }

  private getNewCoachingsWithoutDuplicate(
    fetchedCoachings: Coaching[]
  ): Coaching[] {
    const newCoachingsWithDuplicates: Coaching[] = [
      ...this.state.coachings,
      ...fetchedCoachings,
    ];
    const obj: {} = {};
    newCoachingsWithDuplicates.forEach(
      (content) => (obj[content.id] = content)
    );
    return [
      ...new Set(newCoachingsWithDuplicates.map((coaching) => coaching.id)),
    ].map((id) => obj[id]);
  }
}
